import {
  toWSDateString,
  useModalOldContext,
  WSElement,
  WSElementProps,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  InvoiceRow,
  RecurrenceScheduleDateStatus
} from "@wingspanhq/wingspan-api-sdk";
import classNames from "classnames";
import React, { useMemo } from "react";
import {
  Route,
  useHistory,
  useLocation,
  useRouteMatch
} from "react-router-dom";
import {
  queriesInvoiceRows,
  queriesPayerRows,
  queriesRecurringInvoices
} from "@query";
import { selectorSearchRowName } from "../../../shared/selectors/selectorSearchRowName";
import { WSQueries } from "../../../query/WSQuery";
import { getLocalStartDayByUTC, getStartOfDay } from "../../../utils/dates";
import { InvoicesSeriesInvoiceEdit } from "../../screens/InvoicesSeriesInvoiceEdit";
import { RouteInvoiceDetails } from "../../screens/RouteInvoiceDetails";
import {
  SERIES_INVOICE_ACTIONS_MODAL,
  SeriesInvoiceActionsModal
} from "../SeriesInvoiceActionsModal/SeriesInvoiceActionsModal";
import styles from "./SeriesList.module.scss";

type Props = WSElementProps & {
  recurringInvoiceId: string;
};

export const SeriesList: React.FC<Props> = ({
  recurringInvoiceId,
  ...elementProps
}) => {
  const queryRecurringInvoice =
    queriesRecurringInvoices.getRecurringInvoice.useQuery({
      recurringInvoiceId
    });
  const payerId = queryRecurringInvoice.data?.payerId;
  // Name off the payer search row's server-resolved `profile.resolvedName`,
  // the same string the invoice tables show for this client.
  const queryPayerRow = queriesPayerRows.searchPayerRows.useQuery(
    {
      filter: { id: { eq: payerId! } },
      page: { size: 1 }
    },
    {
      enabled: !!payerId,
      retry: false
    }
  );
  const invoiceIds = useMemo(
    () =>
      (queryRecurringInvoice.data?.schedule?.scheduleDates
        ?.map(scheduleDate => scheduleDate.invoiceId)
        .filter(Boolean) as string[]) || [],
    [queryRecurringInvoice.data?.schedule?.scheduleDates]
  );

  const queryInvoices = queriesInvoiceRows.searchInvoiceRows.useQueryAll(
    { filter: { id: { anyOf: invoiceIds } } },
    { enabled: invoiceIds.length > 0 }
  );
  const queryInvoicesForGate =
    invoiceIds.length === 0
      ? { ...queryInvoices, isSuccess: true, flatData: [] as InvoiceRow[] }
      : queryInvoices;

  const { openModal } = useModalOldContext();
  const history = useHistory();
  const location = useLocation();
  const match = useRouteMatch();

  // Decorative: the client name is left blank rather than taking down the
  // panel when the payer search row cannot be resolved (PLA-3583).
  const payerRow = queryPayerRow.data?.data?.[0];
  const payerName = payerRow ? selectorSearchRowName(payerRow) : "";

  return (
    <WSElement {...elementProps}>
      <SeriesInvoiceActionsModal />

      <Route
        path={`${match.path}/:scheduleDateIndex`}
        component={InvoicesSeriesInvoiceEdit}
        exact
      />
      <Route
        path={`${match.path}/invoice/:invoiceId`}
        component={RouteInvoiceDetails}
        exact
      />
      <WSQueries
        queries={{
          queryRecurringInvoice,
          queryInvoices: queryInvoicesForGate
        }}
      >
        {({
          queryRecurringInvoice: { data: recurringInvoice },
          queryInvoices: { flatData: invoices }
        }) => {
          return (
            <>
              {(recurringInvoice.schedule?.scheduleDates || []).map(
                (scheduleDate, index) => {
                  const invoiceId =
                    scheduleDate.status ===
                      RecurrenceScheduleDateStatus.COMPLETED &&
                    scheduleDate.invoiceId
                      ? scheduleDate.invoiceId
                      : undefined;

                  const invoice = invoiceId
                    ? invoices.find(i => i.id === invoiceId)
                    : undefined;

                  return (
                    <WSElement
                      key={index}
                      className={classNames(styles.item, {
                        [styles.completed]:
                          getLocalStartDayByUTC(scheduleDate.date) <
                          getStartOfDay()
                      })}
                      onClick={() => {
                        if (invoice) {
                          history.push({
                            pathname: `${location.pathname}/invoice/${invoice.id}`,
                            state: {
                              backPath: location.pathname
                            }
                          });
                        } else {
                          openModal(SERIES_INVOICE_ACTIONS_MODAL, {
                            parentInvoiceTemplateId: recurringInvoiceId,
                            scheduleDateIndex: index
                          });
                        }
                      }}
                      mb="XL"
                    >
                      <WSText.ParagraphSm mb="XS">
                        {scheduleDate.status ===
                          RecurrenceScheduleDateStatus.COMPLETED && invoice
                          ? `Due ${toWSDateString(
                              invoice.dueDate,
                              "monthDate"
                            )}`
                          : `${
                              scheduleDate.status ===
                              RecurrenceScheduleDateStatus.SKIPPED
                                ? getLocalStartDayByUTC(scheduleDate.date) >=
                                  getStartOfDay()
                                  ? "Skipping"
                                  : "Skipped"
                                : "Scheduled"
                            } ${toWSDateString(
                              scheduleDate.date,
                              "monthDate"
                            )}`}
                      </WSText.ParagraphSm>
                      <WSText weight="medium" mb="XS">
                        {payerName} – Invoice #
                        {scheduleDate.status ===
                        RecurrenceScheduleDateStatus.SKIPPED
                          ? " Skipped"
                          : invoice
                            ? invoice.invoiceNumber
                            : " Scheduled"}
                      </WSText>
                      <WSText>
                        Invoice {index + 1} of{" "}
                        {recurringInvoice.schedule?.scheduleDates?.length}
                      </WSText>
                    </WSElement>
                  );
                }
              )}
            </>
          );
        }}
      </WSQueries>
    </WSElement>
  );
};
